import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { useClientAuth } from "@/lib/clientAuth";
import { api } from "@/lib/api";
import { LogOut, User, FileText, Package, Plus, RefreshCw, CheckCircle2, CalendarDays } from "lucide-react";
import { Tab } from "@/components/portal/PortalBits";
import { Overview } from "@/components/portal/Overview";
import { QuotesList } from "@/components/portal/QuotesList";
import { ShipmentsList } from "@/components/portal/ShipmentsList";
import { AppointmentsList } from "@/components/portal/AppointmentsList";
import { NewQuoteForm } from "@/components/portal/NewQuoteForm";
import { ProfileForm } from "@/components/portal/ProfileForm";

export default function ClientPortal() {
  const { client, loading, logout, setClient } = useClientAuth();
  const nav = useNavigate();

  const [tab, setTab] = useState("overview");
  const [quotes, setQuotes] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const refreshData = useCallback(async () => {
    setRefreshing(true);
    try {
      const [q, s] = await Promise.all([api.get("/client/quotes"), api.get("/client/shipments")]);
      setQuotes(q.data);
      setShipments(s.data);
    } catch (error) {
      if (error?.response?.status === 401) {
        setClient(false);
        return;
      }
      console.error("Portal data load failed:", error);
      toast.error("Failed to load data");
    } finally {
      setRefreshing(false);
    }
  }, [setClient]);

  useEffect(() => {
    if (client) refreshData();
  }, [client, refreshData]);

  if (loading) return <div className="min-h-screen pt-32 text-center text-slate-500">Loading…</div>;
  if (!client) return <Navigate to="/portal/login" replace />;

  const handleLogout = async () => {
    await logout();
    nav("/portal/login");
  };

  return (
    <div data-testid="client-portal" className="min-h-screen pt-24 pb-24 bg-brand-bg">
      <Toaster position="top-right" richColors />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand">Client Portal</div>
            <h1 className="font-heading text-3xl md:text-4xl font-black text-slate-900 mt-1">
              Welcome, {client.name?.split(" ")[0] || "Client"}
            </h1>
            <div className="text-sm text-slate-600 mt-1">{client.company_name} · {client.email}</div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={refreshData} data-testid="portal-refresh" className="p-2 border border-slate-200 hover:border-primary-brand text-slate-500 hover:text-primary-brand">
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={handleLogout}
              data-testid="portal-logout"
              className="inline-flex items-center gap-2 border border-slate-200 hover:border-primary-brand text-slate-700 hover:text-primary-brand px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        {/* Profile completion banner (shown when key company info is missing) */}
        {(!client.company_name || !client.phone) && (
          <div
            data-testid="profile-incomplete-banner"
            className="mb-6 border border-primary-brand bg-orange-50 p-5 flex flex-col md:flex-row md:items-center justify-between gap-3"
          >
            <div className="flex items-start gap-3">
              <span className="h-8 w-8 flex-none flex items-center justify-center bg-primary-brand text-white font-heading font-black">!</span>
              <div>
                <div className="font-heading font-bold text-slate-900">Complete your company profile</div>
                <div className="text-sm text-slate-700 mt-0.5">
                  Add your company details so we can prepare quotes faster and link your shipments.
                </div>
              </div>
            </div>
            <button
              onClick={() => setTab("profile")}
              data-testid="banner-complete-profile"
              className="bg-primary-brand hover:bg-[#E04F00] text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold"
            >
              Update Profile
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-px bg-slate-200 border border-slate-200 mb-8">
          <Tab active={tab === "overview"} onClick={() => setTab("overview")} icon={CheckCircle2} label="Overview" testid="tab-overview" />
          <Tab active={tab === "quotes"} onClick={() => setTab("quotes")} icon={FileText} label={`My Quotes (${quotes.length})`} testid="tab-my-quotes" />
          <Tab active={tab === "shipments"} onClick={() => setTab("shipments")} icon={Package} label={`My Shipments (${shipments.length})`} testid="tab-my-shipments" />
          <Tab active={tab === "appointments"} onClick={() => setTab("appointments")} icon={CalendarDays} label="Appointments" testid="tab-my-appointments" />
          <Tab active={tab === "new"} onClick={() => setTab("new")} icon={Plus} label="New Quote" testid="tab-new-quote" />
          <Tab active={tab === "profile"} onClick={() => setTab("profile")} icon={User} label="Profile" testid="tab-profile" />
        </div>

        {tab === "overview" && (
          <Overview client={client} quotes={quotes} shipments={shipments} onJump={setTab} />
        )}
        {tab === "quotes" && <QuotesList quotes={quotes} />}
        {tab === "shipments" && <ShipmentsList shipments={shipments} />}
        {tab === "appointments" && <AppointmentsList />}
        {tab === "new" && <NewQuoteForm onCreated={(q) => { setQuotes((qs) => [q, ...qs]); setTab("quotes"); toast.success("Quote request submitted"); }} />}
        {tab === "profile" && <ProfileForm client={client} onSaved={(u) => { setClient(u); toast.success("Profile updated"); }} />}
      </div>
    </div>
  );
}
